"use client";
import useTranslate from "@app/_hooks/useTranslate";

import { Input, Select } from "@components/inputs";
import { Button, Checkbox } from "@components/ngg";
import { useForm, ValidationError } from "@formspree/react";
import type { FormFragment } from "@server/gql/graphql";
import { useCallback, useMemo, useState } from "react";

export default function Return({ reference }: FormFragment & {}) {
  const t = useTranslate();
  const [state, handleSubmit] = useForm(reference || "");
  const [items, setItems] = useState<number[]>(() => [new Date().getTime()]);

  const isInputError = useCallback(
    (inputName: string) => {
      if (!state.errors) return false;
      if (Object.values(state.errors).length === 0) return false;
      return state.errors.getFieldErrors(inputName).length > 0;
    },
    [state.errors],
  );

  const reasons = useMemo(
    () => [
      {
        id: 1,
        value: t("returnFormValueExpectation"),
        label: t("returnFormLabelExpectation"),
      },
      {
        id: 2,
        value: t("returnFormValueNotSuitable"),
        label: t("returnFormLabelNotSuitable"),
      },
      {
        id: 3,
        value: t("returnFormValueMultiple"),
        label: t("returnFormLabelMultiple"),
      },
      {
        id: 4,
        value: t("returnFormValueOther"),
        label: t("returnFormLabelOther"),
      },
    ],
    [t],
  );

  if (state.succeeded) {
    return (
      <div>
        <h2 className="text-lg">{t("formSuccessHeading")}</h2>
        <p className="mt-4 text-base">{t("formSuccessText")}</p>
      </div>
    );
  }

  return (
    <form
      className="not-prose mt-4 max-w-4xl text-base md:mt-6"
      onSubmit={handleSubmit}
    >
      <fieldset className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <Input
            type="email"
            label={t("formEmailLabel")}
            required
            id="email"
            name="email"
            fullWidth
            data-1p-ignore
            classNames={{
              input: isInputError("email") ? "border-error" : "",
            }}
          />
          <ValidationError
            className="mt-2 text-base"
            field="email"
            errors={state.errors}
          />
        </div>
        <div>
          <Input
            type="number"
            label={t("formOrderNumberLabel")}
            required
            id="OrderNo"
            name="OrderNo"
            fullWidth
            hint={t("formOrderNumberHint")}
            inputMode="numeric"
            data-1p-ignore
            classNames={{
              input: isInputError("OrderNo") ? "border-error" : "",
            }}
          />
          <ValidationError
            className="mt-2 text-base"
            field="OrderNo"
            errors={state.errors}
          />
        </div>
      </fieldset>
      <p className="mt-6 text-lg">{t("returnFormInfoText")}</p>
      <div className="mt-6">
        {items.map((item, index) => (
          <fieldset
            key={item}
            className="border-gray-150 border-t bg-gray-100 p-3 pb-6 first:border-t-0"
          >
            <div className="flex w-full items-center justify-between text-sm uppercase">
              <span>{`${t("product")} #${index + 1}`}</span>
              {index > 0 ? (
                <button
                  type="button"
                  className="tracking-tighter text-gray-300 uppercase underline"
                  onClick={() =>
                    setItems((old) => old.filter((i) => i !== item))
                  }
                >
                  {t("remove")}
                </button>
              ) : null}
            </div>
            <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <Select
                  name={`Reason-${index + 1}`}
                  placeholder={t("returnFormReasonForReturnPlaceholder")}
                  options={reasons}
                >
                  <ValidationError
                    className="mt-2 text-base"
                    field={`Reason-${index + 1}`}
                    errors={state.errors}
                  />
                </Select>
              </div>
              <div>
                <Input
                  type="number"
                  label={t("returnFormArticleNumberInputLabel")}
                  required
                  id={`ItemNo-${index + 1}`}
                  name={`ItemNo-${index + 1}`}
                  fullWidth
                  hint={t("returnFormArticleNumberInputHint")}
                  inputMode="numeric"
                  data-1p-ignore
                  classNames={{
                    input: isInputError(`ItemNo-${index + 1}`)
                      ? "border-error"
                      : "",
                  }}
                />
                <ValidationError
                  className="mt-2 text-base"
                  field={`ItemNo-${index + 1}`}
                  errors={state.errors}
                />
              </div>
            </div>
          </fieldset>
        ))}
      </div>

      <fieldset className="mt-3 space-y-6">
        <div className="">
          <button
            type="button"
            className="inline-block text-base underline"
            onClick={() => setItems((i) => [...i, new Date().getTime()])}
          >
            {t("returnFormAddItemButtonText")}
          </button>
        </div>
        <Checkbox
          classNames={{ label: " text-base" }}
          name="confirmation"
          {...{ required: true }}
        >
          {t("returnFormConfirmationCheckboxText")}
        </Checkbox>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Button
            type="submit"
            fullWidth
            isLoading={state.submitting}
            className="btn-primary"
          >
            {t("returnFormCreateReturnButtonText")}
          </Button>
        </div>
      </fieldset>
    </form>
  );
}
